import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { map, Observable, shareReplay } from 'rxjs';
import {
  LoyaltyAttributeDefinitionDto,
  LoyaltyProfile,
  MiddlewareBaseResponse,
  MonetaryPointsData,
  PaginatedDto,
  ProfileLoyaltyAuditLog,
  RoomPointsData,
  StatusPointsData,
} from '@ireckonu/shared-ui';

import { APP_ENV } from '../app-config';
import { LoyaltyMappingService } from './loyalty-mapping.service';
import {
  AttributeDefinitionStatus,
  LoyaltyApiInterface,
  MonetaryPointsDetails,
  PointTransactionsPage,
  RoomNightsDetails,
  StatusPointsDetails,
} from '../models';

const POINT_TRANSACTIONS_PAGE_SIZE = 20;

@Injectable({ providedIn: 'root' })
export class LoyaltyService {
  private readonly http = inject(HttpClient);
  private readonly loyaltyMappingService = inject(LoyaltyMappingService);
  private readonly configApiBaseUrl = inject(APP_ENV).configApiBaseUrl;

  private get loyaltyApiUrl(): string {
    return this.configApiBaseUrl + '/proxy/loyalty/api/v1';
  }

  private readonly loyaltyProfileCache = new Map<
    string,
    Observable<LoyaltyApiInterface>
  >();
  private readonly loyaltyProfileVersion = signal(0);

  /** Signal that consumers can use as rxResource params to trigger reload on invalidation */
  readonly loyaltyProfileInvalidated = this.loyaltyProfileVersion.asReadonly();

  getLoyaltyProfile(externalId: string): Observable<LoyaltyApiInterface> {
    const cacheKey = `loyalty-${externalId}`;

    if (this.loyaltyProfileCache.has(cacheKey)) {
      return this.loyaltyProfileCache.get(cacheKey);
    }

    const params = new HttpParams().set('ExternalId', externalId);

    const request$ = this.http
      .get<
        MiddlewareBaseResponse<PaginatedDto<LoyaltyProfile>>
      >(`${this.loyaltyApiUrl}/loyalty-profiles`, { params })
      .pipe(
        map((response) =>
          this.loyaltyMappingService.mapToLoyaltyDetails(response),
        ),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    this.loyaltyProfileCache.set(cacheKey, request$);
    return request$;
  }

  invalidateLoyaltyProfile(externalId?: string): void {
    if (externalId) {
      this.loyaltyProfileCache.delete(`loyalty-${externalId}`);
    } else {
      this.loyaltyProfileCache.clear();
    }
    this.loyaltyProfileVersion.update((v) => v + 1);
  }

  getMonetaryPointsDetails(
    loyaltyProfileId: number,
    enrollmentId: number,
  ): Observable<MonetaryPointsDetails> {
    return this.http
      .get<
        MiddlewareBaseResponse<MonetaryPointsData>
      >(`${this.loyaltyApiUrl}/loyalty-profiles/${loyaltyProfileId}/enrollments/${enrollmentId}/monetary-points-details`)
      .pipe(
        map((response) =>
          this.loyaltyMappingService.mapToMonetaryPointsDetails(response),
        ),
      );
  }

  getStatusPointsDetails(
    loyaltyProfileId: number,
    enrollmentId: number,
  ): Observable<StatusPointsDetails> {
    return this.http
      .get<
        MiddlewareBaseResponse<StatusPointsData>
      >(`${this.loyaltyApiUrl}/loyalty-profiles/${loyaltyProfileId}/enrollments/${enrollmentId}/status-points-details`)
      .pipe(
        map((response) =>
          this.loyaltyMappingService.mapToStatusPointsDetails(response),
        ),
      );
  }

  getRoomNightsDetails(
    loyaltyProfileId: number,
    enrollmentId: number,
  ): Observable<RoomNightsDetails> {
    return this.http
      .get<
        MiddlewareBaseResponse<RoomPointsData>
      >(`${this.loyaltyApiUrl}/loyalty-profiles/${loyaltyProfileId}/enrollments/${enrollmentId}/room-nights-details`)
      .pipe(
        map((response) =>
          this.loyaltyMappingService.mapToRoomNightsDetails(response),
        ),
      );
  }

  getPointTransactions(
    loyaltyProfileId: number,
    pageNumber = 1,
    pageSize = POINT_TRANSACTIONS_PAGE_SIZE,
  ): Observable<PointTransactionsPage> {
    const params = new HttpParams()
      .set('SortOrder', '2')
      .set('SortColumn', 'Id')
      .set('PageNumber', pageNumber.toString())
      .set('PageSize', pageSize.toString());

    return this.http
      .get<
        MiddlewareBaseResponse<PaginatedDto<ProfileLoyaltyAuditLog>>
      >(`${this.loyaltyApiUrl}/loyalty-profiles/${loyaltyProfileId}/point-transactions`, { params })
      .pipe(
        map((response) =>
          this.loyaltyMappingService.mapToPointTransactions(response),
        ),
      );
  }

  private attributeDefinitions$: Observable<
    LoyaltyAttributeDefinitionDto[]
  > | null = null;

  getAttributeDefinitions(): Observable<LoyaltyAttributeDefinitionDto[]> {
    if (!this.attributeDefinitions$) {
      this.attributeDefinitions$ = this.http
        .get<
          MiddlewareBaseResponse<PaginatedDto<LoyaltyAttributeDefinitionDto>>
        >(`${this.loyaltyApiUrl}/attribute-definitions`)
        .pipe(
          map(
            (response) =>
              response.Data?.Items?.filter(
                (def) =>
                  (def.Status as AttributeDefinitionStatus) ===
                  AttributeDefinitionStatus.Active,
              ) ?? [],
          ),
          shareReplay({ bufferSize: 1, refCount: true }),
        );
    }
    return this.attributeDefinitions$;
  }
}
